import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ImprovedWorkoutCreator from './workout/ImprovedWorkoutCreator';
import { Loader2 } from 'lucide-react';

const validateSetType = (value) => {
  if (value === 0 || value === '0') return 'normal';
  if (value === 1 || value === '1') return 'superset';
  if (value === null || value === '') return 'normal';
  const clean = String(value).trim().toLowerCase();
  const valid = ['normal', 'superset', 'dropset', 'circuit', 'piramidale', 'giant_set'];
  return valid.includes(clean) ? clean : 'normal';
};

const mapTemplateToWorkoutData = (template, availableExercises) => ({
  nome: template.nome || template.name || '',
  descrizione: template.descrizione || template.description || '',
  active: template.active === undefined ? true : !!parseInt(template.active, 10),
  esercizi: (template.esercizi || template.exercises || []).map((ex, index) => {
    const fullExercise = availableExercises.find(
      (e) => parseInt(e.id, 10) === parseInt(ex.esercizio_id, 10)
    );
    const isCardio = fullExercise?.is_cardio === 1;
    let ripetizioni = parseInt(ex.ripetizioni, 10) || 10;
    if (isCardio && ripetizioni > 0) {
      ripetizioni = Math.round(ripetizioni / 60);
    }

    let notes = { trainer: null, user: null, system: null };
    if (ex.note && typeof ex.note === 'string' && ex.note.trim().startsWith('{')) {
      try {
        notes = JSON.parse(ex.note);
      } catch {
        notes = { trainer: ex.note, user: null, system: null };
      }
    } else if (ex.note) {
      notes = { trainer: ex.note, user: null, system: null };
    }

    return {
      ...ex,
      tempId: `ex-${Date.now()}-${index}`,
      esercizio_id: parseInt(ex.esercizio_id, 10),
      serie: parseInt(ex.serie, 10) || (isCardio ? 1 : 3),
      ripetizioni,
      peso: parseFloat(ex.peso) || 0,
      tempo_recupero: parseInt(ex.tempo_recupero, 10) || 90,
      note: ex.note || '',
      notes,
      set_type: validateSetType(ex.set_type),
      linked_to_previous: Boolean(ex.linked_to_previous),
      is_isometric_scheda: ex.is_isometric_scheda || 0,
      calorie: ex.calorie || null,
      bpm: ex.bpm || null,
    };
  }),
});

/**
 * Editor template trial: riusa ImprovedWorkoutCreator con salvataggio su API dedicata.
 */
const TrialTemplateWorkoutAdapter = ({ templateId = null, onClose, onSuccess, mode = 'new' }) => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const [availableExercises, setAvailableExercises] = useState([]);
  const [preloadedWorkoutData, setPreloadedWorkoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const exRes = await api.get('/esercizi.php');
        const exercises = exRes.data || [];
        setAvailableExercises(exercises);

        if (mode === 'edit' && templateId) {
          const tplRes = await api.get(
            `/admin_trial_workout_templates.php?action=get&id=${templateId}`
          );
          setPreloadedWorkoutData(mapTemplateToWorkoutData(tplRes.data, exercises));
        } else {
          setPreloadedWorkoutData({
            nome: '',
            descrizione: '',
            active: true,
            esercizi: [],
          });
        }
      } catch (err) {
        console.error(err);
        setError('Errore nel caricamento del template trial.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, mode, templateId]);

  const handleCustomSave = useCallback(
    async (dataToSend, { mode: saveMode, entityId }) => {
      if (saveMode === 'edit' && entityId) {
        await api.post(`/admin_trial_workout_templates.php?action=update&id=${entityId}`, dataToSend);
      } else {
        await api.post('/admin_trial_workout_templates.php?action=create', dataToSend);
      }
    },
    [api]
  );

  const editorConfig = {
    titleNew: 'Nuovo template trial',
    titleEdit: 'Modifica template trial',
    nomeLabel: 'Nome template*',
    nomePlaceholder: 'es: Scheda base principianti',
    descrizionePlaceholder: 'Descrizione del template per le palestre in trial...',
    activeOn: 'Template visibile ai trial',
    activeOff: 'Template nascosto',
    saveNew: 'Salva template',
    saveEdit: 'Aggiorna template',
    loading: 'Caricamento template...',
    nomeRequired: 'Il nome del template è obbligatorio',
    exercisesRequired: 'Aggiungi almeno un esercizio al template',
    hideUserInTitle: true,
  };

  if (loading || !preloadedWorkoutData) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full text-center border border-gray-200 dark:border-gray-700">
          {error ? (
            <p className="text-red-600 dark:text-red-400">{error}</p>
          ) : (
            <>
              <Loader2 className="animate-spin h-10 w-10 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
              <p className="text-gray-600 dark:text-gray-300">Caricamento...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <ImprovedWorkoutCreator
      user={{ username: 'Template Trial' }}
      onClose={onClose}
      onSuccess={onSuccess}
      mode={mode}
      initialWorkout={mode === 'edit' ? { id: templateId } : null}
      availableExercises={availableExercises}
      api={api}
      onCustomSave={handleCustomSave}
      preloadedWorkoutData={preloadedWorkoutData}
      editorConfig={editorConfig}
    />
  );
};

export default TrialTemplateWorkoutAdapter;
